/**
 * A short muted video on the bartender-kit landing (snippets/kit-loop-video.liquid) that plays like
 * a living photo: in a loop and only while it is on screen, so it loads nothing until it is
 * scrolled to. The button stops and resumes it; with prefers-reduced-motion it starts stopped.
 */
class KitLoopVideo extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #visible = false;
  #stopped = false;

  connectedCallback() {
    this.#stopped = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelector('.kit-loop-video__toggle')?.addEventListener('click', this.#onToggle);

    this.#observer = new IntersectionObserver(
      (entries) => {
        this.#visible = entries.some((entry) => entry.isIntersecting);
        this.#update();
      },
      { threshold: 0.35 }
    );
    this.#observer.observe(this);
    this.#update();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.querySelector('.kit-loop-video__toggle')?.removeEventListener('click', this.#onToggle);
  }

  #onToggle = () => {
    this.#stopped = !this.#stopped;
    this.#update();
  };

  #update() {
    const video = this.querySelector('video');
    if (video) {
      if (this.#visible && !this.#stopped) {
        video.muted = true;
        // A refused play() is the browser's autoplay policy; the poster stays, which is fine
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }

    this.classList.toggle('is-stopped', this.#stopped);
    const button = this.querySelector('.kit-loop-video__toggle');
    const label = this.#stopped ? button?.getAttribute('data-play') : button?.getAttribute('data-pause');
    if (button && label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('kit-loop-video')) {
  customElements.define('kit-loop-video', KitLoopVideo);
}
